// src/pages/KDS/KDSScreen.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ChefHat, Volume2, VolumeX, RefreshCw, LogOut,
  Maximize, Minimize, AlertTriangle, CheckCircle2, X, Eye, EyeOff,
  LayoutGrid, Grid3X3, Grid2X2, ArrowLeftRight, Layers
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useKDSOrders } from '../../hooks/useKDSOrders';
import KDSOrderCard from './components/KDSOrderCard';
import KDSStationFilter from './components/KDSStationFilter';
import toast from 'react-hot-toast';
import KDSConsolidatedView from './components/KDSConsolidatedView';

// Sound options for new orders (auto-populated from public/sounds)
const SOUND_OPTIONS = [
  { label: 'Classic Bell', value: '/sounds/new-order.mp3' },
  { label: 'Order Received (Grab)', value: '/sounds/order_received_grab.mp3' },
  { label: 'Soft Notification', value: '/sounds/universfield-new-notification-025-380251.mp3' },
  { label: 'Pop Notification', value: '/sounds/universfield-new-notification-046-494237.mp3' },
  { label: 'Alert Notification', value: '/sounds/universfield-new-notification-065-494546 (1).mp3' },
  { label: 'Classic Ringtone', value: '/sounds/universfield-ringtone-055-494939.mp3' },
  { label: 'Modern Ringtone', value: '/sounds/universfield-ringtone-065-496263.mp3' },
  { label: 'Beep (Built-in)', value: 'beep' }, // Use Web Audio API beep
];

// Create a beep sound using Web Audio API (fallback if mp3 not available)
const playBeep = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800; // Frequency in Hz
    oscillator.type = 'sine';

    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);

    // Play second beep after short delay
    setTimeout(() => {
      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();
      osc2.connect(gain2);
      gain2.connect(audioContext.destination);
      osc2.frequency.value = 1000;
      osc2.type = 'sine';
      gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      osc2.start(audioContext.currentTime);
      osc2.stop(audioContext.currentTime + 0.3);
    }, 150);
  } catch (e) {
    console.warn('Web Audio API not supported:', e);
  }
};

// Display name — replace MAVERICK_COUNTER with Takeaway, same convention
// used by RecentOrdersModal for counter-mode orders.
const getDisplayTableName = (order) =>
  order.tableName?.toUpperCase().includes('MAVERICK_COUNTER')
    ? 'Takeaway'
    : order.tableName || `Table ${order.tableId}`;

export default function KDSScreen() {
  // Load selected sound from localStorage or default
  const getInitialSound = () => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('kdsSound') || SOUND_OPTIONS[0].value;
    }
    return SOUND_OPTIONS[0].value;
  };
  const navigate = useNavigate();
  const { logout } = useAuth();
  const audioRef = useRef(null);
  const [selectedSound, setSelectedSound] = useState(getInitialSound());

  const [selectedStations, setSelectedStations] = useState(['all']);
  const [itemStatusFilter, setItemStatusFilter] = useState('active'); // active, new, cooking, ready (item-level)
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showReadyItems, setShowReadyItems] = useState(false); // Hide ready items by default
  const [layoutView, setLayoutView] = useState('normal'); // compact, normal, wide
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'consolidated'

  // Layout configuration for different views
  const LAYOUT_CONFIG = {
    compact: {
      grid: 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-7',
      gap: 'gap-2',
      cardSize: 'compact',
      icon: LayoutGrid,
      label: 'Compact'
    },
    normal: {
      grid: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',
      gap: 'gap-4',
      cardSize: 'normal',
      icon: Grid3X3,
      label: 'Normal'
    },
    wide: {
      grid: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4',
      gap: 'gap-4',
      cardSize: 'wide',
      icon: Grid2X2,
      label: 'Wide'
    }
  };

  const currentLayout = LAYOUT_CONFIG[layoutView];

  const {
    orders,
    loading,
    error,
    stations,
    refreshOrders,
    updateOrderStatus,
    updateItemStatus,
    lastUpdated
  } = useKDSOrders(selectedStations, 'active'); // Always fetch all active orders, filter by items on frontend

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Auto-refresh orders every 10 seconds
  useEffect(() => {
    const refreshTimer = setInterval(() => {
      refreshOrders();
    }, 10000);
    return () => clearInterval(refreshTimer);
  }, [refreshOrders]);

  // Play sound for new orders
  const playNewOrderSound = useCallback(() => {
    if (!soundEnabled) return;
    if (selectedSound === 'beep') {
      playBeep();
      return;
    }
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        playBeep();
      });
    } else {
      playBeep();
    }
  }, [soundEnabled, selectedSound]);

  // Track previous order count to detect new orders
  const prevOrderCountRef = useRef(orders.length);
  useEffect(() => {
    if (orders.length > prevOrderCountRef.current) {
      playNewOrderSound();
      toast.success(`New order received!`, { icon: '🔔', duration: 3000 });
    }
    prevOrderCountRef.current = orders.length;
  }, [orders.length, playNewOrderSound]);

  // Handle fullscreen toggle
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Handle status update (bump) - newStatus is passed directly from the card
  const handleBump = async (orderId, newStatus) => {
    try {
      await updateOrderStatus(orderId, newStatus);
    } catch (err) {
      toast.error('Failed to update order status');
    }
  };

  // Helper: Check if order has items matching the filter
  const orderMatchesItemFilter = React.useCallback((order, filter) => {
    const items = order.items || [];
    if (filter === 'active') {
      // Has any non-served item
      return items.some(item => {
        const status = (item.itemStatus || 'PENDING').toUpperCase();
        return status !== 'SERVED';
      });
    } else if (filter === 'new') {
      return items.some(item => (item.itemStatus || 'PENDING').toUpperCase() === 'PENDING');
    } else if (filter === 'cooking') {
      return items.some(item => (item.itemStatus || '').toUpperCase() === 'PREPARING');
    } else if (filter === 'ready') {
      return items.some(item => (item.itemStatus || '').toUpperCase() === 'READY');
    }
    return true;
  }, []);

  // Group orders by table (table-centric view) with item-level filtering
  const groupedTables = React.useMemo(() => {
    const tableMap = {};

    // Filter orders based on item status filter, then group by tableId + tableSplit
    orders
      .filter(order => orderMatchesItemFilter(order, itemStatusFilter))
      .forEach(order => {
        const tableKey = `${order.tableId}-${order.tableSplit || 'A'}`;

        if (!tableMap[tableKey]) {
          tableMap[tableKey] = {
            tableId: order.tableId,
            tableName: getDisplayTableName(order),
            tableSplit: order.tableSplit,
            kots: []
          };
        }

        // Add order as a KOT under this table
        tableMap[tableKey].kots.push(order);
      });

    // Sort KOTs within each table by ID (newest/highest ID first)
    Object.values(tableMap).forEach(table => {
      table.kots.sort((a, b) => b.id - a.id);
    });

    // Convert to array and sort tables by latest KOT ID (newest first)
    return Object.values(tableMap).sort((a, b) => {
      const aLatest = Math.max(...a.kots.map(k => k.id));
      const bLatest = Math.max(...b.kots.map(k => k.id));
      return bLatest - aLatest;
    });
  }, [orders, itemStatusFilter, orderMatchesItemFilter]);

  // Calculate item-level stats for filters
  const itemStats = React.useMemo(() => {
    const stats = { pending: 0, cooking: 0, ready: 0 };
    orders.forEach(order => {
      const items = order.items || [];
      items.forEach(item => {
        const status = (item.itemStatus || 'PENDING').toUpperCase();
        if (status === 'PENDING') stats.pending++;
        else if (status === 'PREPARING') stats.cooking++;
        else if (status === 'READY') stats.ready++;
      });
    });
    return stats;
  }, [orders]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Handle item status update (PENDING -> PREPARING -> READY -> SERVED)
  const handleUpdateItemStatus = async (orderId, itemId, newStatus) => {
    try {
      await updateItemStatus(orderId, itemId, newStatus);
    } catch (err) {
      toast.error('Failed to update item status');
    }
  };

  // Remove a station from selection
  const removeStation = (stationId) => {
    const newSelection = selectedStations.filter(id => id !== stationId);
    if (newSelection.length === 0) {
      setSelectedStations(['all']);
    } else {
      setSelectedStations(newSelection);
    }
  };

  // Get station name by ID
  const getStationName = (stationId) => {
    const station = stations.find(s => s.id?.toString() === stationId);
    return station?.name || stationId;
  };

  // Check if specific stations are selected (not "all")
  const hasSpecificStations = !selectedStations.includes('all') && selectedStations.length > 0;

  // Update audio element src when selectedSound changes
  useEffect(() => {
    if (selectedSound !== 'beep' && audioRef.current) {
      audioRef.current.src = selectedSound;
    }
  }, [selectedSound]);

  // Persist selected sound
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('kdsSound', selectedSound);
    }
  }, [selectedSound]);

  // Play sound immediately when user selects a new sound
  const handleSoundChange = (e) => {
    const value = e.target.value;
    setSelectedSound(value);
    setTimeout(() => {
      if (value === 'beep') {
        playBeep();
      } else if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {
          playBeep();
        });
      }
    }, 100); // slight delay to ensure audio src updates
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      {/* Audio element for sounds */}
      <audio ref={audioRef} src={selectedSound !== 'beep' ? selectedSound : undefined} preload="auto" />

      {/* Header - Compact Single Row */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-2 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          {/* Left: Logo & Title */}
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-teal-500 rounded flex items-center justify-center">
              <ChefHat size={16} className="text-white" />
            </div>
            <h1 className="text-sm font-semibold text-white">KDS</h1>
          </div>

          {/* Station Filter Dropdown */}
          <KDSStationFilter
            stations={stations}
            selectedStations={selectedStations}
            onStationChange={setSelectedStations}
          />

          {/* Selected Station Badges */}
          {hasSpecificStations && (
            <div className="flex items-center gap-1 flex-wrap">
              {selectedStations.map(stationId => (
                <span
                  key={stationId}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-teal-500/20 text-teal-400 text-xs font-medium rounded"
                >
                  {getStationName(stationId)}
                  <button
                    onClick={() => removeStation(stationId)}
                    className="hover:text-teal-200 transition-colors"
                  >
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Item Status Filter Tabs */}
          <div className="flex items-center gap-1">
            {[
              { key: 'active', label: 'All', count: itemStats.pending + itemStats.cooking + itemStats.ready },
              { key: 'new', label: 'New', count: itemStats.pending, color: 'teal' },
              { key: 'cooking', label: 'Cooking', count: itemStats.cooking, color: 'amber' },
              { key: 'ready', label: 'Ready', count: itemStats.ready, color: 'emerald' },
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setItemStatusFilter(tab.key)}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                  itemStatusFilter === tab.key
                    ? 'bg-teal-500 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                {tab.label}
                <span className={`ml-1 ${
                  itemStatusFilter === tab.key ? 'text-teal-100' : tab.color === 'teal' ? 'text-teal-400' : tab.color === 'amber' ? 'text-amber-400' : tab.color === 'emerald' ? 'text-emerald-400' : 'text-slate-500'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Time */}
          <div className="text-xs font-mono text-slate-300">
            {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
          </div>

          {/* Controls */}
          <div className="flex items-center gap-1">
            {/* Sound Selector */}
            <select
              value={selectedSound}
              onChange={handleSoundChange}
              className="px-2 py-1 rounded text-xs font-medium bg-slate-700 text-slate-200 border border-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500 mr-2"
              title="Select New Order Sound"
              style={{ minWidth: 120 }}
            >
              {SOUND_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {/* Toggle Ready Items */}
            <button
              onClick={() => setShowReadyItems(!showReadyItems)}
              className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors ${
                showReadyItems
                  ? 'text-emerald-400 bg-emerald-500/10'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
              title={showReadyItems ? 'Hide Ready Items' : 'Show Ready Items'}
            >
              {showReadyItems ? <Eye size={14} /> : <EyeOff size={14} />}
              <span className="hidden sm:inline">Ready Items {itemStats.ready > 0 && `(${itemStats.ready})`}</span>
            </button>

            {/* Consolidated Screen Toggle */}
            <button
              onClick={() => setViewMode(v => (v === 'table' ? 'consolidated' : 'table'))}
              className={`p-1.5 rounded transition-colors ${
                viewMode === 'consolidated'
                  ? 'bg-teal-500 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }`}
              title="Consolidated Screen"
            >
              <Layers size={16} />
            </button>

            <div className="w-px h-4 bg-slate-600 mx-1" />

            {/* Layout View Selector */}
            <div className="flex items-center bg-slate-700/50 rounded-md p-0.5">
              {Object.entries(LAYOUT_CONFIG).map(([key, config]) => {
                const Icon = config.icon;
                const isActive = layoutView === key;
                return (
                  <button
                    key={key}
                    onClick={() => setLayoutView(key)}
                    className={`p-1.5 rounded transition-all duration-200 ${
                      isActive
                        ? 'bg-teal-500 text-white shadow-sm'
                        : 'text-slate-400 hover:text-white hover:bg-slate-600'
                    }`}
                    title={config.label}
                  >
                    <Icon size={14} />
                  </button>
                );
              })}
            </div>

            <div className="w-px h-4 bg-slate-600 mx-1" />

            <button
              onClick={refreshOrders}
              disabled={loading}
              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
              title="Refresh"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            </button>

            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className={`p-1.5 rounded transition-colors ${
                soundEnabled ? 'text-teal-400 bg-teal-500/10' : 'text-slate-400 hover:bg-slate-700'
              }`}
              title={soundEnabled ? 'Sound On' : 'Sound Off'}
            >
              {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>

            <button
              onClick={toggleFullscreen}
              className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <Minimize size={16} /> : <Maximize size={16} />}
            </button>

            <div className="w-px h-4 bg-slate-600 mx-1" />

            <button
              onClick={() => navigate('/select-role')}
              className="p-1.5 text-slate-400 hover:text-teal-400 hover:bg-teal-500/10 rounded transition-colors"
              title="Switch Portal"
            >
              <ArrowLeftRight size={16} />
            </button>

            <button
              onClick={handleLogout}
              className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
              title="Logout"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-auto p-4">
        {loading && orders.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 relative">
                <div className="absolute inset-0 rounded-full border-4 border-slate-700"></div>
                <div className="absolute inset-0 rounded-full border-4 border-teal-500 border-t-transparent animate-spin"></div>
              </div>
              <p className="text-slate-400">Loading orders...</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <AlertTriangle size={40} className="mx-auto mb-3 text-red-500" />
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={refreshOrders}
                className="px-4 py-2 bg-teal-500 hover:bg-teal-400 text-white rounded-lg text-sm"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : viewMode === 'consolidated' ? (
          <KDSConsolidatedView
            orders={orders}
            itemStatusFilter={itemStatusFilter}
            showReadyItems={showReadyItems}
            onUpdateItemStatus={handleUpdateItemStatus}
            gridClass={currentLayout.grid}
            gapClass={currentLayout.gap}
          />
        ) : groupedTables.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <CheckCircle2 size={48} className="mx-auto mb-3 text-emerald-500" />
              <h3 className="text-lg font-semibold text-slate-300 mb-1">All Caught Up!</h3>
              <p className="text-slate-500 text-sm">No pending orders at the moment</p>
            </div>
          </div>
        ) : (
          <div className={`grid ${currentLayout.grid} ${currentLayout.gap}`}>
            {groupedTables.map(table => (
              <KDSOrderCard
                key={`${table.tableId}-${table.tableSplit || 'A'}`}
                table={table}
                onUpdateItemStatus={handleUpdateItemStatus}
                showReadyItems={showReadyItems}
                cardSize={currentLayout.cardSize}
              />
            ))}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-800 border-t border-slate-700 px-6 py-2 flex-shrink-0">
        <div className="flex items-center justify-between text-xs text-slate-400">
          <div className="flex items-center gap-3">
            <span>Last updated: {lastUpdated ? new Date(lastUpdated).toLocaleTimeString() : 'Never'}</span>
            <span className="text-slate-600">|</span>
            <span>Auto-refresh: 10s</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
            <span>Connected</span>
          </div>
        </div>
      </footer>
    </div>
  );
}